import type { UIMessage } from "@convex-dev/agent";
import { sanitizeVisibleAssistantText } from "../shared/assistant-visible-text-sanitize";
import type { MessengerBubbleItem } from "./messenger-bubbles";
import { messengerToolSteps } from "./messenger-tool-steps";

/** A `useUIMessages` row; the app's api only types the paging shape. */
type UIMessageRow = Pick<UIMessage, "role" | "parts">;

/** The full UI message the messenger queries return, checked instead of cast. */
export function isFullUIMessage<T extends UIMessageRow>(
  row: T,
): row is T & UIMessage {
  const full: Partial<UIMessage> = row;
  return (
    typeof full.key === "string" &&
    typeof full.text === "string" &&
    typeof full._creationTime === "number"
  );
}

/** Chat row → bubble; an agent turn without text yet still shows while it runs tools. */
export function toMessengerBubble(message: UIMessage): MessengerBubbleItem | null {
  if (message.role !== "user" && message.role !== "assistant") return null;
  const text = (message.role === "assistant" ? sanitizeVisibleAssistantText(message.text) : message.text).trim();
  const tools = message.role === "assistant" ? messengerToolSteps(message.parts) : [];
  if (text.length === 0 && tools.length === 0) return null;
  const live = message.status === "streaming" || message.status === "pending";
  return {
    id: message.key,
    author: message.role === "user" ? "self" : "agent",
    name: null,
    text,
    at: live ? null : message._creationTime,
    ...(tools.length > 0 ? { tools } : {}),
    ...(live && message.role === "assistant" ? { streaming: true } : {}),
  };
}

/** Last row still waits for the answer and no tool spinner says so already. */
export function isAwaitingAgentAnswer(last: UIMessage | undefined): boolean {
  if (last === undefined) return false;
  if (last.role === "user") return true;
  if (last.role !== "assistant" || last.text.trim() !== "") return false;
  return !messengerToolSteps(last.parts).some((step) => step.state === "running");
}
